/**
 * "Confirm your role" at the end of `/learn/access` (13 Oct): the roster
 * entries that name the person (site owner for USLAG, approver for 365FO
 * Finance × DK, HR validator for DK, member of a team…), each with "This is
 * me" / "Not me – the right person is ___" and a comment. The answers go to
 * IT Admin → Access training progress; this replaces the old mailto loop.
 */

import { CircleCheck, Icon } from "@norican/icons";
import { Alert, Button, Field, Textarea, Typography } from "@norican/react";
import { useState } from "react";

import { ErrorState, LoadingState, SectionCard } from "@/components/common";
import { EmployeePicker } from "@/features/tickets";
import { useT } from "@/i18n";
import type { RoleConfirmationView, RosterEntryView } from "@/lib/api";

import { useConfirmRoster, useMyRoster } from "./api";

function EntryRow({
  entry,
  answered,
}: {
  entry: RosterEntryView;
  answered: RoleConfirmationView | undefined;
}) {
  const { t } = useT();
  const confirm = useConfirmRoster();
  const [notMe, setNotMe] = useState(false);
  const [who, setWho] = useState<{ id: string; name: string } | null>(null);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(answered === undefined);
  const missingWho = notMe && who === null && comment.trim() === "";

  const send = (decision: "me" | "not_me") => {
    confirm.mutate(
      {
        roster_key: entry.key,
        decision,
        suggested_person: decision === "not_me" ? (who?.name ?? null) : null,
        suggested_rec_id: decision === "not_me" ? (who?.id ?? null) : null,
        comment: comment.trim() || null,
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <li className="border-border-subtle flex flex-col gap-3 border-b pb-4 last:border-b-0">
      <Typography as="p" variant="body" className="font-medium" lang="en">
        {entry.label}
      </Typography>
      {!editing && answered ? (
        <div className="flex flex-wrap items-center gap-3">
          <Typography as="p" variant="body-sm" className="flex items-center gap-1.5">
            <Icon icon={CircleCheck} size="sm" tone="success" />
            {answered.decision === "me"
              ? t("accessTraining.confirm.answeredMe")
              : t("accessTraining.confirm.answeredNotMe", {
                  name: answered.suggested_person ?? "—",
                })}
          </Typography>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            {t("accessTraining.confirm.change")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={confirm.isPending && !notMe} onClick={() => send("me")}>
              {t("accessTraining.confirm.isMe")}
            </Button>
            <Button
              size="sm"
              variant={notMe ? "subtle" : "outline"}
              aria-expanded={notMe}
              onClick={() => setNotMe((v) => !v)}
            >
              {t("accessTraining.confirm.notMe")}
            </Button>
          </div>
          {notMe ? (
            <Field name={`right-${entry.key}`} label={t("accessTraining.confirm.rightPerson")}>
              <EmployeePicker
                name={`right-${entry.key}`}
                value={who?.id ?? null}
                onChange={(picked) =>
                  setWho(picked ? { id: picked.id, name: picked.display_name } : null)
                }
              />
            </Field>
          ) : null}
          <Field name={`comment-${entry.key}`} label={t("accessTraining.confirm.comment")}>
            <Textarea
              value={comment}
              rows={2}
              maxLength={2000}
              onChange={(event) => setComment(event.target.value)}
            />
          </Field>
          {notMe ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                disabled={missingWho}
                loading={confirm.isPending}
                onClick={() => send("not_me")}
              >
                {t("accessTraining.confirm.send")}
              </Button>
              {missingWho ? (
                <Typography as="span" variant="caption" tone="muted">
                  {t("accessTraining.confirm.needWho")}
                </Typography>
              ) : null}
            </div>
          ) : null}
          {confirm.isError ? <ErrorState error={confirm.error} variant="inline" /> : null}
        </div>
      )}
    </li>
  );
}

export function ConfirmRoles() {
  const { t } = useT();
  const roster = useMyRoster();

  return (
    <section
      id="confirm"
      aria-labelledby="confirm-title"
      className="scroll-mt-[calc(var(--app-header-height,4rem)+var(--learn-bar-height,3rem)+1rem)]"
    >
      <SectionCard
        title={<span id="confirm-title">{t("accessTraining.confirm.title")}</span>}
        description={t("accessTraining.confirm.intro")}
        accentTone="accent"
      >
        {roster.isPending ? (
          <LoadingState />
        ) : roster.isError ? (
          <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
        ) : (
          <div className="flex flex-col gap-3">
            {roster.data.partial ? (
              <Alert tone="warning">{t("accessTraining.confirm.partial")}</Alert>
            ) : null}
            {roster.data.entries.length === 0 ? (
              <Typography as="p" variant="body-sm" tone="muted">
                {t("accessTraining.confirm.none")}
              </Typography>
            ) : (
              <ul className="flex flex-col gap-4">
                {roster.data.entries.map((entry) => (
                  <EntryRow
                    key={entry.key}
                    entry={entry}
                    answered={roster.data.confirmations.find(
                      (c) => c.roster_key === entry.key && c.state !== "dismissed",
                    )}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </SectionCard>
    </section>
  );
}
